import * as React from "react";

import KEY from "/src/constants/keyboard-keydown-key";
import {css} from "/src/utils/utils";
import translate from "/src/utils/translate";

import Icon, {IconSVG} from "../../icon/Icon";

import ChevronRightIcon from "/src/svg/chevron-right.svg";

import * as styles from "./mobile-navigation-selector.module.scss";

export enum ItemsCaretPosition {
  LEFT = "LEFT",
  RIGHT = "RIGHT",
}

interface MobileNavigationSelectorProps {
  disabled: boolean;
  numberOfItems: number;
  label: string;
  onSelect?: (index: number) => void;
  children: (focusedListItem: number) => React.ReactNode;
  onOpen: () => void;
  onClose: () => void;
  itemsCaretPosition?: ItemsCaretPosition;
  leftIconSvg?: IconSVG;
}

const MOBILE_NAV_ROW_HEIGHT = 60;

function MobileNavigationSelector(
  props: MobileNavigationSelectorProps
): React.ReactElement {
  const {
    disabled,
    numberOfItems,
    label,
    onSelect,
    children,
    leftIconSvg,
    itemsCaretPosition = ItemsCaretPosition.RIGHT,
    onOpen,
    onClose,
  } = props;

  const [expanded, setExpanded] = React.useState<boolean>(false);
  const [focusedListItem, setFocusedListItem] = React.useState<number>(-1);

  const wrapperElemRef = React.useRef<null | HTMLDivElement>(null);
  const listElementRef = React.useRef<null | HTMLDivElement>(null);

  const handleToggleExpand = React.useCallback(() => {
    // toggle expand status
    if (expanded) {
      onClose();
      setExpanded(false);
    } else {
      onOpen();
      setExpanded(true);
    }
  }, [expanded, onClose, onOpen]);

  const shouldBeExpanded = expanded && !disabled;

  const onKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (!listElementRef.current) {
        return;
      }

      switch (event.key) {
        case KEY.UP: {
          event.preventDefault();
          const tempIdx = focusedListItem > 0 ? focusedListItem - 1 : 0;
          setFocusedListItem(tempIdx);
          break;
        }
        case KEY.DOWN: {
          event.preventDefault();
          const tempIdx =
            focusedListItem < numberOfItems ? focusedListItem + 1 : 0;
          setFocusedListItem(tempIdx);
          break;
        }
        case KEY.HOME:
          event.preventDefault();
          setFocusedListItem(0);
          break;
        case KEY.END:
          event.preventDefault();
          setFocusedListItem(numberOfItems - 1);
          break;
        case KEY.ESC:
          event.preventDefault();
          setExpanded(false);
          setFocusedListItem(-1);
          break;
        case KEY.SPACE:
        case KEY.ENTER:
          event.preventDefault();
          if (event.key === KEY.ENTER && focusedListItem !== -1) {
            if (onSelect) {
              onSelect(focusedListItem);
            }
            setExpanded(false);
            setFocusedListItem(-1);
            return;
          }

          setExpanded(true);
          break;
        default:
          break;
      }
    },
    [focusedListItem, numberOfItems, onSelect]
  );

  React.useEffect(() => {
    // get current list item height
    if (!wrapperElemRef.current || !listElementRef.current) {
      return;
    }

    const height = `${
      MOBILE_NAV_ROW_HEIGHT +
      (shouldBeExpanded ? listElementRef.current.offsetHeight : 0)
    }px`;

    wrapperElemRef.current.style.height = height;
  }, [shouldBeExpanded]);

  return (
    <div
      className={css(
        styles.mobileNavigationSelector,
        shouldBeExpanded ? styles.mobileNavigationSelectorExpanded : ""
      )}
      ref={wrapperElemRef}
    >
      <div className={css(styles.mobileNavigationSelector__wrapper)}>
        <button
          className={css(styles.mobileNavigationSelector__btn)}
          type="button"
          aria-haspopup="listbox"
          aria-label={translate("Language")}
          aria-expanded={shouldBeExpanded}
          onClick={handleToggleExpand}
          onKeyDown={onKeyDown}
        >
          {leftIconSvg && (
            <Icon
              role="presentation"
              classes={styles.mobileNavigationSelector__leftIcon}
              svg={leftIconSvg}
            />
          )}
          {label}
          <Icon
            role="presentation"
            classes={styles.mobileNavigationSelector__rightIcon}
            svg={ChevronRightIcon}
          />
        </button>
      </div>
      <div
        ref={listElementRef}
        className={css(styles.mobileNavigationSelector__itemsWrapper)}
      >
        <div
          className={css(
            itemsCaretPosition === ItemsCaretPosition.LEFT
              ? styles.mobileNavigationSelector__itemsCaretLeft
              : styles.mobileNavigationSelector__itemsCaretRight
          )}
        />

        <ul
          className={css(styles.mobileNavigationSelector__items)}
          role="listbox"
          aria-label={translate("Language")}
          tabIndex={shouldBeExpanded ? 0 : -1}
        >
          {children(focusedListItem)}
        </ul>
      </div>
    </div>
  );
}

export default MobileNavigationSelector;
